// -----------------------------------------------------------------------------
// ModifyAppelOffreModal.jsx – 4 étapes identiques à AddAppelOffreForm
// -----------------------------------------------------------------------------
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Paper,
  Typography,
  styled,
  IconButton,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
} from "@mui/material";
import { Close as CloseIcon, Check } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import {
  fetchAllAppelOffres,
  updateExistingAppelOffre,
} from "../../redux/appeloffreSlice";

/* ───  design tokens (mêmes que Add) ─────────────────────────────────────── */
const NAVY       = "#2F4E8B";
const NAVY_LIGHT = "#4a6bad";
const LINE_GREY  = "#C6CDD6";
const LABEL_GREY = "#4F5565";
const OUTLINE    = "#D4D9E0";

/* ───  champ texte arrondi ──────────────────────────────────────────────── */
const CustomTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    transition: "all .25s ease",
    "& fieldset": { borderColor: OUTLINE },
    "&:hover fieldset": { borderColor: NAVY_LIGHT },
    "&.Mui-focused fieldset": { borderColor: NAVY_LIGHT },
  },
  "& .MuiInputBase-input": { fontSize: 14 },
});

/* ───  même GradientPaper que dans AddAppelOffreForm  ───────────────────── */
const GradientPaper = ({ children, sx }) => (
  <Paper
    elevation={8}
    sx={{
      borderRadius: 3,
      p: { xs: 3, md: 4 },
      width: { xs: "100%", md: 820 },
      mx: "auto",
      background:
        "linear-gradient(135deg, #FFFFFF 0%, #F6F9FC 60%, #E9F0F9 100%)",
      position: "relative",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

/* ───  stepper custom ───────────────────────────────────────────────────── */
const AppelConnector = styled(StepConnector)({
  top: 14,
  "& .MuiStepConnector-line": {
    borderColor: LINE_GREY,
    borderTopWidth: 2,
    borderRadius: 1,
  },
});

const StepIconRoot = styled("div")(({ ownerState }) => ({
  zIndex: 1,
  width: 28,
  height: 28,
  borderRadius: "50%",
  border: `2px solid ${NAVY}`,
  backgroundColor: ownerState.completed || ownerState.active ? NAVY : "#FFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: ownerState.completed || ownerState.active ? "#FFF" : NAVY,
  fontWeight: 700,
  fontSize: 13,
}));
const StepIconComponent = (props) => {
  const { active, completed, icon } = props;
  return (
    <StepIconRoot ownerState={{ active, completed }}>
      {completed ? <Check fontSize="small" /> : icon}
    </StepIconRoot>
  );
};

const steps       = ["Informations générales", "Adresse", "Détails", "Commentaire"];
const stepNumbers = ["01", "02", "03", "04"];

/* ────────────────────────────────────────────────────────────────────────── */
export default function ModifyAppelOffreModal({ open, handleClose, appelOffre, onSuccess })
{
  const dispatch = useDispatch();

  const [activeStep, setActiveStep] = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");

  /* ------ état formulaire ------------------------------------------------ */
  const [formData, setFormData] = useState({
    client_final: "",
    reference: "",
    reference_client: "",
    date_publication: "",
    date_cloture_offre: "",
    lieu_prestation: "",
    titre: "",
    descriptif: "",
    annee_experiences: "",
    tjm: "",
    commentaire: "",
    date_saisie: "",
    mode: "draft",
  });

  /* pré-remplissage ------------------------------------------------------- */
  useEffect(() => {
    if (open && appelOffre) {
      setFormData({ ...appelOffre });
      setActiveStep(0);
      setError("");
    }
  }, [open, appelOffre]);

  /* handlers -------------------------------------------------------------- */
  const onChange = (e) =>
    setFormData((s) => ({ ...s, [e.target.name]: e.target.value }));

  const next = () => setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      await dispatch(
        updateExistingAppelOffre({ id: appelOffre.id, appelOffreData: formData })
      ).unwrap();
      onSuccess?.();
      handleClose();
    } catch {
      setError("Erreur lors de la mise à jour.");
    } finally {
      setLoading(false);
      dispatch(fetchAllAppelOffres());
    }
  };

  /* rendu par étape ------------------------------------------------------- */
  const renderStep = (s) => {
    switch (s) {
      case 0:
        return (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" },
              gap: 2,
            }}
          >
            <CustomTextField label="Client Final"      name="client_final"       value={formData.client_final}       onChange={onChange} fullWidth />
            <CustomTextField label="Référence"         name="reference"          value={formData.reference}          onChange={onChange} fullWidth />
            <CustomTextField label="Référence Client"  name="reference_client"   value={formData.reference_client}   onChange={onChange} fullWidth />
            <CustomTextField label="Date de Publication" name="date_publication" type="date" value={formData.date_publication} onChange={onChange} InputLabelProps={{ shrink: true }} fullWidth />
            <CustomTextField label="Date de Clôture"   name="date_cloture_offre" type="date" value={formData.date_cloture_offre} onChange={onChange} InputLabelProps={{ shrink: true }} fullWidth />
            <CustomTextField label="Titre"             name="titre"              value={formData.titre}              onChange={onChange} fullWidth />
            <Box gridColumn="span 3">
              <CustomTextField label="Descriptif" name="descriptif" value={formData.descriptif} onChange={onChange} multiline rows={3} fullWidth />
            </Box>
          </Box>
        );
      case 1:
        return (
          <CustomTextField label="Adresse" name="lieu_prestation" value={formData.lieu_prestation} onChange={onChange} fullWidth />
        );
      case 2:
        return (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" },
              gap: 2,
            }}
          >
            <CustomTextField label="Année Expérience" name="annee_experiences" type="number" value={formData.annee_experiences} onChange={onChange} fullWidth />
            <CustomTextField label="TJM"              name="tjm"               type="number" value={formData.tjm}               onChange={onChange} fullWidth />
            <CustomTextField select label="Mode" name="mode" value={formData.mode} onChange={onChange} fullWidth>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="publié">Publié</MenuItem>
              <MenuItem value="interne">Interne</MenuItem>
            </CustomTextField>
          </Box>
        );
      case 3:
        return (
          <CustomTextField label="Commentaire" name="commentaire" value={formData.commentaire} onChange={onChange} multiline rows={4} fullWidth />
        );
      default:
        return null;
    }
  };

  /* ------------------------------- RENDER -------------------------------- */
  return (
    <GradientPaper>
      {/* icône close */}
      <IconButton
        onClick={handleClose}
        sx={{ position: "absolute", top: 16, right: 16, color: LABEL_GREY }}
      >
        <CloseIcon />
      </IconButton>

      {/* titre */}
      <Typography variant="h5" align="center" sx={{ fontWeight: 700, color: NAVY, mb: 3 }}>
        Modifier un appel d&apos;offre
      </Typography>

      {/* stepper */}
      <Stepper activeStep={activeStep} alternativeLabel connector={<AppelConnector />} sx={{ mb: 4 }}>
        {steps.map((label, idx) => (
          <Step key={label}>
            <StepLabel
              StepIconComponent={StepIconComponent}
              StepIconProps={{ icon: stepNumbers[idx] }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* champs courants */}
      <Box sx={{ mb: 4 }}>{renderStep(activeStep)}</Box>

      {/* navigation */}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        {activeStep > 0 ? (
          <Button
            variant="outlined"
            onClick={back}
            sx={{
              borderColor: OUTLINE,
              color: LABEL_GREY,
              textTransform: "none",
              px: 4,
              fontWeight: "bold",
            }}
          >
            Précédent
          </Button>
        ) : <Box />}

        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={next}
            sx={{
              backgroundColor: NAVY,
              textTransform: "none",
              px: 5,
              fontWeight: "bold",
              "&:hover": { backgroundColor: NAVY_LIGHT },
            }}
          >
            Suivant
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              backgroundColor: NAVY,
              textTransform: "none",
              px: 5,
              fontWeight: "bold",
              "&:hover": { backgroundColor: NAVY_LIGHT },
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "#FFF" }} />
            ) : (
              "Modifier"
            )}
          </Button>
        )}
      </Box>
    </GradientPaper>
  );
}
